import * as React from 'react';
import { Alert, Badge, Col, Progress, Row } from 'reactstrap';
import { AccentButton } from '../controls/AccentButton';
import { AccentCheckUnbound } from '../controls/unbound/AccentCheckUnbound';
import { update } from '../services/DataService';
import { accentUtils, from, showOK, t } from '../services/HelperService';
import { configFrom, getProduct, loadProductModel, productVersionSort, removeFromModelCache } from '../services/ProductService';
import { printSettingProductItemShowEmptyRow } from '../services/TemplateService';
import { AccentDialog } from './AccentDialog';

const getProductCode = (product) => product.ProductCode ?? product.Code ?? "";

const getProductKey = (product) => `${product.supplierName ?? ""}_${product.productCode ?? ""}`;

const getImportItemKey = (item) => `${item.type ?? ""}_${item.name ?? ""}`;

const normalize = (value) => (value ?? "").toLowerCase();

const codeKey = (...parts) => parts.map(p => normalize(p)).join("|");

const getTypeTitle = (type) => {
    if (accentUtils.isEmpty(type)) return "No Type";

    return t(type);
};

const getProductVersionLabel = (product) => {
    if (product.IsMarketplaceProduct) return "Marketplace";
    if (product.IsDraft) return "Draft";
    if (product.IsActiveVersion) return `Active v${product.Version ?? ""}`;
    return `v${product.Version ?? ""}`;
};

const emptyGuid = "00000000-0000-0000-0000-000000000000";

const buildExportProducts = (exportFile) => {
    return from(exportFile?.productPrintSettings ?? [])
        .select(x => x.product)
        .where(p => !accentUtils.isNull(p))
        .groupBy(getProductKey)
        .select(g => g.getSource()[0])
        .orderBy(p => p.supplierName ?? "")
        .thenBy(p => p.productCode ?? "")
        .toArray();
};

const getPreferredLocalProduct = (exportProduct, localProducts) => {
    const productMatches = localProducts.filter(p => normalize(getProductCode(p)) === normalize(exportProduct.productCode));

    if (productMatches.length === 0) {
        return { match: null, candidates: [] };
    }

    const supplierMatches = productMatches.filter(p => normalize(p.Supplier) === normalize(exportProduct.supplierName));
    const versionCandidates = supplierMatches.length > 0 ? supplierMatches : productMatches;
    const productGroups = from(versionCandidates)
        .groupBy(p => p.ID)
        .select(g => {
            const versions = from(g.getSource()).orderBy(productVersionSort).toArray();
            const match = versions.find(p => p.IsDraft)
                ?? versions.find(p => p.IsActiveVersion)
                ?? versions.find(p => (p.VersionID ?? emptyGuid).toUpperCase() === emptyGuid)
                ?? versions[0];

            return {
                id: g.key(),
                match,
                versions
            };
        })
        .toArray();

    if (productGroups.length > 1) {
        return {
            match: null,
            candidates: productGroups.map(g => g.match)
        };
    }

    const productGroup = productGroups[0];

    return {
        match: productGroup.match,
        candidates: [productGroup.match],
        versions: productGroup.versions
    };
};

const buildTemplateMatches = (exportFile, localTemplates) => {
    return from(exportFile?.documentTemplates ?? [])
        .select(template => {
            const match = (localTemplates ?? []).find(local =>
                normalize(local.Type) === normalize(template.type)
                && normalize(local.Name) === normalize(template.name));

            return {
                template,
                match,
                exists: !accentUtils.isNull(match),
                selected: accentUtils.isNull(match)
            };
        })
        .orderBy(m => m.template.type ?? "")
        .thenBy(m => m.template.name ?? "")
        .toArray();
};

const buildPrintSettingMatches = (exportFile, localPrintSettings) => {
    return from(exportFile?.printSettings ?? [])
        .select(printSetting => {
            const match = (localPrintSettings ?? []).find(local =>
                normalize(local.Type) === normalize(printSetting.type)
                && normalize(local.Name) === normalize(printSetting.name));

            return {
                printSetting,
                match,
                exists: !accentUtils.isNull(match),
                selected: true
            };
        })
        .orderBy(m => m.printSetting.type ?? "")
        .thenBy(m => m.printSetting.name ?? "")
        .toArray();
};

const buildImportOptionLookup = (model) => {
    const optionByCode = {};
    const valueByCode = {};

    if (accentUtils.isNull(model)) {
        return { optionByCode, valueByCode };
    }

    const groups = configFrom(model.GetProductOptionGroups()).toArray();

    groups.forEach(group => {
        const options = configFrom(model.GetProductOptions(group.ID)).toArray();

        options.forEach(option => {
            optionByCode[codeKey(group.Code, option.Code)] = option.ID;

            const values = configFrom(model.GetProductOptionValues(option.ID)).toArray();

            values.forEach(value => {
                valueByCode[codeKey(group.Code, option.Code, value.Code)] = value.ID;
            });
        });
    });

    return { optionByCode, valueByCode };
};

const renderIssueContext = (issue) => {
    if (!accentUtils.isNull(issue.documentTemplate)) {
        return <div>
            <div style={{ fontWeight: "bolder" }}>Document Template: {issue.documentTemplate.name}</div>
            <div style={{ opacity: 0.7 }}>{issue.documentTemplate.type}</div>
        </div>;
    }

    if (!accentUtils.isNull(issue.printSetting)) {
        return <div>
            <div style={{ fontWeight: "bolder" }}>Print Setting: {issue.printSetting.name}</div>
            <div style={{ opacity: 0.7 }}>{issue.printSetting.type ?? "No type"}</div>
        </div>;
    }

    if (!accentUtils.isNull(issue.product)) {
        return <div>
            <div style={{ fontWeight: "bolder" }}>Product: {issue.product.productName}</div>
            <div style={{ opacity: 0.7 }}>{issue.product.supplierName} / {issue.product.productCode || "No product code"}</div>
        </div>;
    }

    if (!accentUtils.isNull(issue.memberProductName) || !accentUtils.isNull(issue.memberProductCode)) {
        return <div>
            <div style={{ fontWeight: "bolder" }}>Member Product: {issue.memberProductName ?? "Unknown"}</div>
            <div style={{ opacity: 0.7 }}>{issue.memberProductCode ?? "No product code"}</div>
        </div>;
    }

    if (!accentUtils.isNull(issue.context?.documentTemplate)) {
        return <div>
            <div style={{ fontWeight: "bolder" }}>Document Template: {issue.context.documentTemplate.name}</div>
            <div style={{ opacity: 0.7 }}>{issue.context.documentTemplate.type}</div>
        </div>;
    }

    return <span style={{ opacity: 0.7 }}>No record context</span>;
};

export class ProductPrintSettingsImportDlg extends React.Component {

    constructor(props) {
        super(props);

        const exportProducts = buildExportProducts(props.exportFile);
        const templateMatches = buildTemplateMatches(props.exportFile, props.documentTemplates ?? []);
        const printSettingMatches = buildPrintSettingMatches(props.exportFile, props.printSettings ?? []);
        const productMatches = exportProducts.map(exportProduct => {
            const { match, candidates } = getPreferredLocalProduct(exportProduct, props.products ?? []);

            return {
                exportProduct,
                match,
                candidates,
                selected: !accentUtils.isNull(match) && candidates.length === 1
            };
        });

        this.state = {
            templateMatches,
            printSettingMatches,
            productMatches,
            importing: false,
            completedSteps: 0,
            totalSteps: 0,
            currentStep: "",
            importSummary: null
        };

        this.onClose = this.onClose.bind(this);
        this.onProceed = this.onProceed.bind(this);
        this.onTypeSelectionChanged = this.onTypeSelectionChanged.bind(this);
        this.onTypeCategorySelectionChanged = this.onTypeCategorySelectionChanged.bind(this);
        this.onTemplateSelectionChanged = this.onTemplateSelectionChanged.bind(this);
        this.onPrintSettingSelectionChanged = this.onPrintSettingSelectionChanged.bind(this);
        this.onProductSelectionChanged = this.onProductSelectionChanged.bind(this);
        this.onSupplierSelectionChanged = this.onSupplierSelectionChanged.bind(this);
    }

    onClose(e) {
        if (e.promise) e.promise();
        this.props.onClose({ canceled: true });
    }

    async onProceed(e) {
        if (!accentUtils.isNull(this.state.importSummary)) {
            if (e.promise) e.promise();
            this.props.onClose({ canceled: false, importSummary: this.state.importSummary });
            return;
        }

        const selectedProductMatches = this.state.productMatches.filter(m => m.selected);
        const invalidSelectedProductMatches = selectedProductMatches.filter(m => accentUtils.isNull(m.match) || m.candidates.length > 1);

        if (invalidSelectedProductMatches.length > 0) {
            showOK(
                "Invalid Product Selection",
                "One or more selected products have no local match or multiple possible matches. Please unselect those products before proceeding.",
                ["application_strings.application.buttons.ok"],
                false,
                null,
                true,
                600
            ).then(() => {
                if (e.promise) e.promise();
            });

            return;
        }

        await this.runImport(selectedProductMatches);

        if (e.promise) e.promise();
    }

    getImportSteps(selectedProductMatches) {
        const templatesToCreate = this.state.templateMatches.filter(m => m.selected && !m.exists).map(m => m.template);
        const printSettings = this.state.printSettingMatches.filter(m => m.selected).map(m => m.printSetting);
        const selectedPrintSettingKeys = printSettings.map(getImportItemKey);
        const productPrintSettings = [];

        selectedProductMatches.forEach(productMatch => {
            (this.props.exportFile?.productPrintSettings ?? [])
                .filter(x => selectedPrintSettingKeys.includes(getImportItemKey(x.printSetting)))
                .filter(x => getProductKey(x.product) === getProductKey(productMatch.exportProduct))
                .filter(x => (x.items ?? []).length > 0)
                .forEach(x => productPrintSettings.push({ productMatch, productPrintSetting: x }));
        });

        return { templatesToCreate, printSettings, productPrintSettings };
    }

    async runImport(selectedProductMatches) {
        const steps = this.getImportSteps(selectedProductMatches);
        const totalSteps = steps.templatesToCreate.length + steps.printSettings.length + steps.productPrintSettings.length;
        const summary = {
            templatesCreated: 0,
            templatesSkipped: this.state.templateMatches.filter(m => m.exists).length,
            printSettingsCreated: 0,
            printSettingsUpdated: 0,
            productSettingsImported: 0,
            warnings: []
        };
        const printSettingIDs = {};

        this.setState({ importing: true, completedSteps: 0, totalSteps, currentStep: "Starting import...", importSummary: null });

        for (const template of steps.templatesToCreate) {
            this.setState({ currentStep: `Creating template ${template.name}` });

            const result = await update("ImportDocumentTemplateSettings_DocumentTemplate", {
                Name: template.payload?.Name ?? template.name,
                Type: template.payload?.Type ?? template.type,
                TemplateID: template.payload?.TemplateID,
                TemplateParams: accentUtils.isNull(template.payload?.TemplateParams) ? null : JSON.stringify(template.payload.TemplateParams)
            });

            if (result?.Action === "Created") summary.templatesCreated++;

            this.incrementProgress();
        }

        for (const printSetting of steps.printSettings) {
            this.setState({ currentStep: `Importing print setting ${printSetting.name}` });

            const result = await update("ImportDocumentTemplateSettings_PrintSetting", {
                Name: printSetting.payload?.Name ?? printSetting.name,
                Type: printSetting.payload?.Type ?? printSetting.type
            });

            printSettingIDs[codeKey(printSetting.type, printSetting.name)] = result?.ID;

            if (result?.Action === "Created") summary.printSettingsCreated++;
            if (result?.Action === "Updated") summary.printSettingsUpdated++;

            this.incrementProgress();
        }

        for (const step of steps.productPrintSettings) {
            await this.importProductPrintSetting(step, printSettingIDs, summary);
            this.incrementProgress();
        }

        this.setState({ importing: false, currentStep: "Import complete", importSummary: summary });
    }

    incrementProgress() {
        this.setState(s => ({ completedSteps: s.completedSteps + 1 }));
    }

    async importProductPrintSetting(step, printSettingIDs, summary) {
        const { productMatch, productPrintSetting } = step;
        const localProduct = productMatch.match;
        const productVersionID = emptyGuid;
        const printSettingID = printSettingIDs[codeKey(productPrintSetting.printSetting.type, productPrintSetting.printSetting.name)];
        const memberLookups = {};

        if (accentUtils.isNull(printSettingID)) {
            summary.warnings.push(`Print setting not found for ${productPrintSetting.printSetting.name}`);
            return;
        }

        this.setState({ currentStep: `Importing ${productPrintSetting.printSetting.name} / ${localProduct.ProductName}` });

        let model = null;

        try {
            model = await loadProductModel(localProduct.ID, productVersionID);
        } catch (err) {
            summary.warnings.push(`Could not load product model for ${localProduct.ProductName}: ${err?.message ?? err}`);
            return;
        }

        if (accentUtils.isNull(model)) {
            summary.warnings.push(`Could not load product model for ${localProduct.ProductName}`);
            return;
        }

        try {
            const lookup = buildImportOptionLookup(model);
            const items = [];

            for (const item of productPrintSetting.items ?? []) {
                const mappedItem = await this.mapImportProductPrintSettingItem(item, lookup, localProduct, productVersionID, memberLookups, summary);

                if (!accentUtils.isNull(mappedItem)) {
                    items.push(mappedItem);
                }
            }

            const saveResult = await update("SavePrintSettings", {
                PrintSettingID: printSettingID,
                Settings: {
                    ProductID: localProduct.ID,
                    ProductVersionID: productVersionID,
                    ShowEmptyRow: productPrintSetting.productSettings?.showEmptyRow ?? printSettingProductItemShowEmptyRow.hide,
                    Items: items
                }
            });

            if (saveResult !== true) {
                summary.warnings.push(`Failed to save print settings for ${productPrintSetting.printSetting.name} / ${localProduct.ProductName}`);
                return;
            }

            summary.productSettingsImported++;
        } finally {
            removeFromModelCache(localProduct.ID, productVersionID);
            Object.keys(memberLookups).forEach(key => {
                const memberLookup = memberLookups[key];
                removeFromModelCache(memberLookup.product.ID, memberLookup.productVersionID);
            });
        }
    }

    async getMemberImportLookup(item, localProduct, memberLookups, summary) {
        if (accentUtils.isEmpty(item.memberProductCode)) {
            return null;
        }

        const memberProduct = (localProduct.MemberProducts ?? [])
            .map(member => getProduct(member.MemberProductID, member.MemberVersionID))
            .find(product => normalize(getProductCode(product)) === normalize(item.memberProductCode));

        if (accentUtils.isNull(memberProduct)) {
            summary.warnings.push(`Could not resolve member product ${item.memberProductCode} for ${localProduct.ProductName}`);
            return null;
        }

        const memberProductVersionID = memberProduct.VersionID ?? "00000000-0000-0000-0000-000000000000";
        const memberKey = `${memberProduct.ID}_${memberProductVersionID}`;

        if (accentUtils.isNull(memberLookups[memberKey])) {
            const memberModel = await loadProductModel(memberProduct.ID, memberProductVersionID);

            if (accentUtils.isNull(memberModel)) {
                summary.warnings.push(`Could not load member product model for ${memberProduct.ProductName}`);
                return null;
            }

            memberLookups[memberKey] = {
                product: memberProduct,
                productVersionID: memberProductVersionID,
                lookup: buildImportOptionLookup(memberModel)
            };
        }

        return memberLookups[memberKey];
    }

    async mapImportProductPrintSettingItem(item, lookup, localProduct, productVersionID, memberLookups, summary) {
        const memberLookup = await this.getMemberImportLookup(item, localProduct, memberLookups, summary);
        const itemLookup = memberLookup?.lookup ?? lookup;
        let itemID = item.itemCode ?? item.field;

        if (item.itemType === "ProductOption") {
            itemID = itemLookup.optionByCode[codeKey(item.groupCode, item.optionCode)];
        }

        if (item.itemType === "ProductOptionValue") {
            itemID = itemLookup.valueByCode[codeKey(item.groupCode, item.optionCode, item.valueCode)];
        }

        if (accentUtils.isEmpty(itemID)) {
            summary.warnings.push(`Could not resolve ${item.itemType} ${item.groupCode ?? ""} / ${item.optionCode ?? ""} for ${localProduct.ProductName}`);
            return null;
        }

        return {
            ProductID: localProduct.ID,
            ProductVersionID: productVersionID,
            ProductName: localProduct.ProductName,
            GroupName: item.groupName ?? item.groupCode ?? "",
            Field: item.optionName ?? item.optionCode ?? item.field ?? item.itemCode ?? "",
            ItemID: `${itemID}`,
            SortIndex: item.sortIndex,
            Show: item.showAs,
            ShowIfEmpty: item.showIfEmpty,
            Label: item.label,
            Source: item.source,
            MemberProductID: memberLookup?.product.ID,
            MemberProductVersionID: memberLookup?.productVersionID,
            AdditionalSettings: accentUtils.isNull(item.additionalSettings) ? null : {
                Type: item.additionalSettings.type ?? "fabric",
                ShowFabricWidth: item.additionalSettings.showFabricWidth,
                ShowPatternRepeat: item.additionalSettings.showPatternRepeat
            }
        };
    }

    onProductSelectionChanged(e) {
        const selectedKey = e.tagName;

        this.setState({
            productMatches: this.state.productMatches.map(m => {
                const matchKey = getProductKey(m.exportProduct);

                if (matchKey !== selectedKey) {
                    return m;
                }

                return { ...m, selected: e.checked };
            })
        });
    }

    onSupplierSelectionChanged(e) {
        const supplier = e.tagName;

        this.setState({
            productMatches: this.state.productMatches.map(m => {
                if ((m.exportProduct.supplierName ?? "") !== supplier) {
                    return m;
                }

                if (accentUtils.isNull(m.match) || m.candidates.length > 1) {
                    return { ...m, selected: false };
                }

                return { ...m, selected: e.checked };
            })
        });
    }

    onTypeSelectionChanged(e) {
        const type = e.tagName;

        this.setState({
            templateMatches: this.state.templateMatches.map(m => {
                if ((m.template.type ?? "") !== type || m.exists) return m;

                return { ...m, selected: e.checked };
            }),
            printSettingMatches: this.state.printSettingMatches.map(m => {
                if ((m.printSetting.type ?? "") !== type) return m;

                return { ...m, selected: e.checked };
            })
        });
    }

    onTypeCategorySelectionChanged(e) {
        const [category, type] = e.tagName.split("|");

        if (category === "templates") {
            this.setState({
                templateMatches: this.state.templateMatches.map(m => {
                    if ((m.template.type ?? "") !== type || m.exists) return m;

                    return { ...m, selected: e.checked };
                })
            });
        }

        if (category === "printSettings") {
            this.setState({
                printSettingMatches: this.state.printSettingMatches.map(m => {
                    if ((m.printSetting.type ?? "") !== type) return m;

                    return { ...m, selected: e.checked };
                })
            });
        }
    }

    onTemplateSelectionChanged(e) {
        const templateKey = e.tagName;

        this.setState({
            templateMatches: this.state.templateMatches.map(m => {
                if (getImportItemKey(m.template) !== templateKey || m.exists) return m;

                return { ...m, selected: e.checked };
            })
        });
    }

    onPrintSettingSelectionChanged(e) {
        const printSettingKey = e.tagName;

        this.setState({
            printSettingMatches: this.state.printSettingMatches.map(m => {
                if (getImportItemKey(m.printSetting) !== printSettingKey) return m;

                return { ...m, selected: e.checked };
            })
        });
    }

    renderIssues() {
        const issues = this.props.exportFile?.issues ?? [];

        if (issues.length === 0) {
            return <Alert color="success">No issues were reported in the export file.</Alert>;
        }

        return <div>
            <Alert color="warning">This export file contains {issues.length} issue(s). Review these before proceeding.</Alert>
            <table className="table table-sm table-hover">
                <thead>
                    <tr>
                        <th>Severity</th>
                        <th>Code</th>
                        <th>Record</th>
                        <th>Message</th>
                    </tr>
                </thead>
                <tbody>
                    {issues.map((issue, i) => <tr key={i}>
                        <td><Badge color={issue.severity === "warning" ? "warning" : "secondary"}>{issue.severity}</Badge></td>
                        <td>{issue.code}</td>
                        <td>{renderIssueContext(issue)}</td>
                        <td>{issue.message}</td>
                    </tr>)}
                </tbody>
            </table>
        </div>;
    }

    renderProductMatches() {
        const productGroups = from(this.state.productMatches)
            .groupBy(m => m.exportProduct.supplierName ?? "")
            .select(g => ({
                supplier: g.key(),
                matches: from(g.getSource()).orderBy(m => m.exportProduct.productName ?? "").toArray()
            }))
            .orderBy(g => g.supplier)
            .toArray();

        const rows = productGroups.map(g => {
            const selectableMatches = g.matches.filter(m => !accentUtils.isNull(m.match) && m.candidates.length === 1);
            const selectedMatches = selectableMatches.filter(m => m.selected);
            const isSupplierSelected = selectableMatches.length > 0 && selectedMatches.length === selectableMatches.length;

            return <React.Fragment key={g.supplier}>
                <tr style={{ backgroundColor: "#f7f7f7" }}>
                    <td style={{ width: "35px" }}>
                        <AccentCheckUnbound
                            key={`${g.supplier}_${isSupplierSelected}_${selectedMatches.length}`}
                            tagName={g.supplier}
                            defaultValue={isSupplierSelected}
                            disabled={selectableMatches.length === 0}
                            onChange={this.onSupplierSelectionChanged}
                        />
                    </td>
                    <td colSpan={3}>
                        <span style={{ fontWeight: "bolder" }}>{accentUtils.isEmpty(g.supplier) ? "No Supplier" : g.supplier}</span>
                        <Badge color="secondary" style={{ marginLeft: "8px" }}>{selectedMatches.length} / {g.matches.length}</Badge>
                    </td>
                </tr>
            {g.matches.map(m => {
                const matchKey = getProductKey(m.exportProduct);
                const hasMatch = !accentUtils.isNull(m.match);
                const isAmbiguous = m.candidates.length > 1;

                return <React.Fragment key={matchKey}>
                    <tr>
                        <td style={{ width: "35px" }}></td>
                        <td>
                            <div className="d-flex align-items-center" style={{ paddingLeft: "28px", gap: "5px" }}>
                                <AccentCheckUnbound
                                    key={`${matchKey}_${m.selected}`}
                                    tagName={matchKey}
                                    defaultValue={m.selected}
                                    disabled={!hasMatch || isAmbiguous}
                                    onChange={this.onProductSelectionChanged}
                                />
                                <div>
                                    <div style={{ fontWeight: "bolder" }}>{m.exportProduct.productName}</div>
                                    <div style={{ opacity: 0.7 }}>{m.exportProduct.productCode}</div>
                                </div>
                            </div>
                        </td>
                        <td>
                            {hasMatch && <React.Fragment>
                                <div style={{ fontWeight: "bolder" }}>{m.match.ProductName}</div>
                                <div style={{ opacity: 0.7 }}>{m.match.Supplier} / {getProductCode(m.match)} / {getProductVersionLabel(m.match)}</div>
                            </React.Fragment>}
                            {!hasMatch && <Badge color="danger">No product code match</Badge>}
                        </td>
                        <td>
                            {hasMatch && !isAmbiguous && <Badge color="success">Matched</Badge>}
                            {hasMatch && isAmbiguous && <Badge color="warning">Multiple matches</Badge>}
                        </td>
                    </tr>
                    {isAmbiguous && <tr>
                        <td></td>
                        <td colSpan={3}>
                            <div style={{ paddingLeft: "55px" }}>
                                <div style={{ fontWeight: "bolder", paddingBottom: "5px" }}>Possible local matches</div>
                                <table className="table table-sm table-bordered">
                                    <thead>
                                        <tr>
                                            <th>Product</th>
                                            <th>Supplier</th>
                                            <th>Code</th>
                                            <th>Version</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {m.candidates.map(candidate => <tr key={`${candidate.ID}_${candidate.VersionID}`}>
                                            <td>{candidate.ProductName}</td>
                                            <td>{candidate.Supplier}</td>
                                            <td>{getProductCode(candidate)}</td>
                                            <td>{getProductVersionLabel(candidate)}</td>
                                        </tr>)}
                                    </tbody>
                                </table>
                            </div>
                        </td>
                    </tr>}
                </React.Fragment>;
            })}
        </React.Fragment>;
        });

        return <table className="table table-sm table-hover">
            <thead>
                <tr>
                    <th></th>
                    <th>Export Product</th>
                    <th>Matched Local Product</th>
                    <th>Status</th>
                </tr>
            </thead>
            <tbody>
                {rows}
                {rows.length === 0 && <tr><td colSpan={4}>No product print settings found in this export file.</td></tr>}
            </tbody>
        </table>;
    }

    renderTypeScope() {
        const typeKeys = [...new Set([
            ...this.state.templateMatches.map(m => m.template.type ?? ""),
            ...this.state.printSettingMatches.map(m => m.printSetting.type ?? "")
        ])].sort();

        const rows = typeKeys.map(type => {
            const templateMatches = from(this.state.templateMatches)
                .where(m => (m.template.type ?? "") === type)
                .orderBy(m => m.template.name ?? "")
                .toArray();
            const printSettingMatches = from(this.state.printSettingMatches)
                .where(m => (m.printSetting.type ?? "") === type)
                .orderBy(m => m.printSetting.name ?? "")
                .toArray();

            const selectableTemplates = templateMatches.filter(m => !m.exists);
            const selectedTemplates = selectableTemplates.filter(m => m.selected);
            const selectedPrintSettings = printSettingMatches.filter(m => m.selected);
            const selectableCount = selectableTemplates.length + printSettingMatches.length;
            const selectedCount = selectedTemplates.length + selectedPrintSettings.length;
            const isTypeSelected = selectableCount > 0 && selectedCount === selectableCount;
            const templatesSelected = selectableTemplates.length > 0 && selectedTemplates.length === selectableTemplates.length;
            const printSettingsSelected = printSettingMatches.length > 0 && selectedPrintSettings.length === printSettingMatches.length;

            return <React.Fragment key={type}>
                <tr style={{ backgroundColor: "#f7f7f7" }}>
                    <td style={{ width: "35px" }}>
                        <AccentCheckUnbound
                            key={`type_${type}_${isTypeSelected}_${selectedCount}`}
                            tagName={type}
                            defaultValue={isTypeSelected}
                            disabled={selectableCount === 0}
                            onChange={this.onTypeSelectionChanged}
                        />
                    </td>
                    <td>
                        <span style={{ fontWeight: "bolder" }}>{getTypeTitle(type)}</span>
                        <Badge color="secondary" style={{ marginLeft: "8px" }}>{selectedCount} / {selectableCount}</Badge>
                    </td>
                    <td></td>
                </tr>
                <tr>
                    <td style={{ width: "35px" }}></td>
                    <td>
                        <div className="d-flex align-items-center" style={{ paddingLeft: "28px", gap: "5px" }}>
                            <AccentCheckUnbound
                                key={`templates_${type}_${templatesSelected}_${selectedTemplates.length}`}
                                tagName={`templates|${type}`}
                                defaultValue={templatesSelected}
                                disabled={selectableTemplates.length === 0}
                                onChange={this.onTypeCategorySelectionChanged}
                            />
                            <span style={{ fontWeight: "bolder" }}>Templates</span>
                            <Badge color="secondary">{selectedTemplates.length} / {templateMatches.length}</Badge>
                        </div>
                    </td>
                    <td></td>
                </tr>
                {templateMatches.map(m => {
                    const templateKey = getImportItemKey(m.template);

                    return <tr key={`template_${templateKey}`}>
                        <td style={{ width: "35px" }}></td>
                        <td>
                            <div className="d-flex align-items-center" style={{ paddingLeft: "55px", gap: "5px" }}>
                                <AccentCheckUnbound
                                    key={`${templateKey}_${m.selected}`}
                                    tagName={templateKey}
                                    defaultValue={m.selected}
                                    disabled={m.exists}
                                    onChange={this.onTemplateSelectionChanged}
                                />
                                <span>{m.template.name}</span>
                            </div>
                        </td>
                        <td>
                            {m.exists && <Badge color="secondary">Exists / Skip</Badge>}
                            {!m.exists && <Badge color="success">Create</Badge>}
                        </td>
                    </tr>;
                })}
                <tr>
                    <td style={{ width: "35px" }}></td>
                    <td>
                        <div className="d-flex align-items-center" style={{ paddingLeft: "28px", gap: "5px" }}>
                            <AccentCheckUnbound
                                key={`printSettings_${type}_${printSettingsSelected}_${selectedPrintSettings.length}`}
                                tagName={`printSettings|${type}`}
                                defaultValue={printSettingsSelected}
                                disabled={printSettingMatches.length === 0}
                                onChange={this.onTypeCategorySelectionChanged}
                            />
                            <span style={{ fontWeight: "bolder" }}>Print Settings</span>
                            <Badge color="secondary">{selectedPrintSettings.length} / {printSettingMatches.length}</Badge>
                        </div>
                    </td>
                    <td></td>
                </tr>
                {printSettingMatches.map(m => {
                    const printSettingKey = getImportItemKey(m.printSetting);

                    return <tr key={`printSetting_${printSettingKey}`}>
                        <td style={{ width: "35px" }}></td>
                        <td>
                            <div className="d-flex align-items-center" style={{ paddingLeft: "55px", gap: "5px" }}>
                                <AccentCheckUnbound
                                    key={`${printSettingKey}_${m.selected}`}
                                    tagName={printSettingKey}
                                    defaultValue={m.selected}
                                    onChange={this.onPrintSettingSelectionChanged}
                                />
                                <span>{m.printSetting.name}</span>
                            </div>
                        </td>
                        <td>
                            {m.exists && <Badge color="warning">Update Draft</Badge>}
                            {!m.exists && <Badge color="success">Create Draft</Badge>}
                        </td>
                    </tr>;
                })}
            </React.Fragment>;
        });

        return <table className="table table-sm table-hover">
            <thead>
                <tr>
                    <th></th>
                    <th>Document Type / Name</th>
                    <th>Status</th>
                </tr>
            </thead>
            <tbody>
                {rows}
                {rows.length === 0 && <tr><td colSpan={3}>No templates or print settings found in this export file.</td></tr>}
            </tbody>
        </table>;
    }

    renderTemplateMatches() {
        const templateGroups = from(this.state.templateMatches)
            .groupBy(m => m.template.type ?? "")
            .select(g => ({
                type: g.key(),
                matches: from(g.getSource()).orderBy(m => m.template.name ?? "").toArray()
            }))
            .orderBy(g => g.type)
            .toArray();

        const rows = templateGroups.map(g => <React.Fragment key={g.type}>
            <tr style={{ backgroundColor: "#f7f7f7" }}>
                <td colSpan={3}>
                    <span style={{ fontWeight: "bolder" }}>{getTypeTitle(g.type)}</span>
                    <Badge color="secondary" style={{ marginLeft: "8px" }}>{g.matches.length}</Badge>
                </td>
            </tr>
            {g.matches.map(m => <tr key={`${m.template.type}_${m.template.name}`}>
                <td>{m.template.name}</td>
                <td>{m.template.type}</td>
                <td>
                    {m.exists && <Badge color="secondary">Exists</Badge>}
                    {!m.exists && <Badge color="success">Create</Badge>}
                </td>
            </tr>)}
        </React.Fragment>);

        return <table className="table table-sm table-hover">
            <thead>
                <tr>
                    <th>Template Name</th>
                    <th>Type</th>
                    <th>Status</th>
                </tr>
            </thead>
            <tbody>
                {rows}
                {rows.length === 0 && <tr><td colSpan={3}>No templates found in this export file.</td></tr>}
            </tbody>
        </table>;
    }

    renderImportProgress() {
        if (!this.state.importing && accentUtils.isNull(this.state.importSummary)) return null;

        const progressValue = this.state.totalSteps === 0 ? 0 : Math.round((this.state.completedSteps / this.state.totalSteps) * 100);
        const warningGroups = accentUtils.isNull(this.state.importSummary) ? [] : from(this.state.importSummary.warnings)
            .groupBy(w => w)
            .select(g => ({ message: g.key(), count: g.getSource().length }))
            .orderByDescending(g => g.count)
            .toArray();

        return <Row>
            <Col md={12}>
                <h5>Import Progress</h5>
                <Progress value={progressValue}>{progressValue}%</Progress>
                <div style={{ opacity: 0.7, paddingTop: "8px" }}>
                    {this.state.currentStep}
                </div>
                <div style={{ opacity: 0.7 }}>
                    Completed {this.state.completedSteps} of {this.state.totalSteps}
                </div>
                {!accentUtils.isNull(this.state.importSummary) && <Alert color="success" style={{ marginTop: "10px" }}>
                    <div>Import complete.</div>
                    <div>Templates created: {this.state.importSummary.templatesCreated}</div>
                    <div>Templates skipped: {this.state.importSummary.templatesSkipped}</div>
                    <div>Print settings created: {this.state.importSummary.printSettingsCreated}</div>
                    <div>Print settings updated: {this.state.importSummary.printSettingsUpdated}</div>
                    <div>Product settings imported: {this.state.importSummary.productSettingsImported}</div>
                    <div>Warnings: {this.state.importSummary.warnings.length}</div>
                </Alert>}
                {warningGroups.length > 0 && <Alert color="warning">
                    <div style={{ fontWeight: "bolder", paddingBottom: "8px" }}>Import warnings</div>
                    <table className="table table-sm table-hover">
                        <thead>
                            <tr>
                                <th style={{ width: "80px" }}>Count</th>
                                <th>Warning</th>
                            </tr>
                        </thead>
                        <tbody>
                            {warningGroups.map(w => <tr key={w.message}>
                                <td>{w.count}</td>
                                <td>{w.message}</td>
                            </tr>)}
                        </tbody>
                    </table>
                </Alert>}
            </Col>
        </Row>;
    }

    render() {
        const selectedProductCount = this.state.productMatches.filter(m => m.selected).length;
        const showImportProgressOnly = this.state.importing || !accentUtils.isNull(this.state.importSummary);

        return (<AccentDialog
            headerContent="Import Document Templates / Print Settings"
            onClose={this.onClose}
            relativeWidth={d => d.width - 80}
            relativeHeight={d => d.height - 80}
            actionsContent={[
                <AccentButton key="proceed" tagName="importPreviewProceedBtn" disabled={this.state.importing} onClick={this.onProceed}>
                    {this.state.importSummary ? "Done" : "Proceed"}
                </AccentButton>
            ]}
        >
            {showImportProgressOnly && this.renderImportProgress()}
            {!showImportProgressOnly && <React.Fragment>
                <Row>
                    <Col md={12}>
                        <Alert color="info">
                            Templates will only be created when they do not already exist. Existing templates will not be updated.
                            Print settings will be created or updated in drafts.
                        </Alert>
                    </Col>
                </Row>
                <Row>
                    <Col md={12}>
                        <h5>File Issues</h5>
                        {this.renderIssues()}
                    </Col>
                </Row>
                <Row>
                    <Col md={12}>
                        <h5>Type Scope</h5>
                        {this.renderTypeScope()}
                    </Col>
                </Row>
                <Row>
                    <Col md={12}>
                        <h5>Product Matches</h5>
                        <div style={{ opacity: 0.7, paddingBottom: "10px" }}>{selectedProductCount} product match(es) selected</div>
                        {this.renderProductMatches()}
                    </Col>
                </Row>
            </React.Fragment>}
        </AccentDialog>);
    }
}
